// auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from './auth.service';

// --- Module-level variables to synchronize refresh attempts ---
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
const REFRESH_FAILED_MARKER = '__REFRESH_FAILED__';

// This will help prevent multiple "session expired" dialogs from stacking
let sessionExpiredShown = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const confirmationService = inject(ConfirmationService);
  const dialogService = inject(DialogService);

  // Attach the access token if available
  const accessToken = authService.getAccessToken();
  const clonedRequest = accessToken
    ? req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
      })
    : req;

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // We only want to handle 401 errors that are not themselves refresh calls
      const isAuthEndpoint = req.url.includes('/auth/');
      if (error.status === 401 && !isAuthEndpoint) {
        return handle401Error(clonedRequest, next, authService, confirmationService, dialogService);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: Parameters<HttpInterceptorFn>[0],
  nextFn: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  confirmationService: ConfirmationService,
  dialogService: DialogService
): Observable<any> {
  // If we are NOT already refreshing…
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null); // reset

    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      // No refresh token – session is definitely expired.
      refreshTokenSubject.next(REFRESH_FAILED_MARKER);
      openSessionExpiredDialog(confirmationService, authService, dialogService);
      isRefreshing = false;
      return throwError(() => new Error('Session expired: no refresh token'));
    }

    // Try refreshing the token (timeout so cold-start / hung refresh doesn't leave UI stuck)
    return authService.refreshTokens().pipe(
      timeout(20000),
      switchMap((tokens: any) => {
        // Refresh succeeded
        isRefreshing = false;
        refreshTokenSubject.next(tokens.accessToken);

        // Retry the original request with the new token
        return nextFn(
          request.clone({
            headers: request.headers.set('Authorization', `Bearer ${tokens.accessToken}`),
          })
        );
      }),
      catchError((err) => {
        // Refresh failed
        isRefreshing = false;
        refreshTokenSubject.next(REFRESH_FAILED_MARKER);
        openSessionExpiredDialog(confirmationService, authService, dialogService);
        return throwError(() => err);
      })
    );
  } else {
    // If we are ALREADY refreshing, queue up requests until refresh is done
    return refreshTokenSubject.pipe(
      filter((token) => token !== null), // wait until new token is set
      take(1),
      switchMap((token) => {
        if (token === REFRESH_FAILED_MARKER) {
          return throwError(() => new Error('Session expired: refresh failed'));
        }

        // Retry the original request with the new token
        return nextFn(
          request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          })
        );
      })
    );
  }
}

function openSessionExpiredDialog(confirmationService: ConfirmationService, authService: AuthService, dialogService: DialogService): void {
  if (!sessionExpiredShown) {
    sessionExpiredShown = true;
    // Close all open modals (e.g. Add case) so only the session-expired message is visible
    closeAllDialogs(dialogService);
    confirmationService.confirm({
      header: 'Session Expired',
      message: 'Your session has expired. Please log in again.',
      acceptLabel: 'OK',
      rejectVisible: false,
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => {
        sessionExpiredShown = false;
        authService.logout().subscribe();
      }
    });
  }
}

function closeAllDialogs(dialogService: DialogService): void {
  try {
    const refs = Array.from(dialogService.dialogComponentRefMap?.keys() ?? []);
    refs.forEach((ref) => ref.close(null));
  } catch {
    // ignore
  }
}
